import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";

interface Props {
  aiConfigId: string;
}

interface TabItem {
  label: string;
  path: string;
  disabled?: boolean;
}

export default function AINewSettingsTabs({ aiConfigId }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [stickyOffset, setStickyOffset] = useState(0);

  const tabs: TabItem[] = [
    { label: "General", path: `/ai-new/settings/general/${aiConfigId}` },
    { label: "Channel Overrides", path: `/ai-new/settings/channel-overrides/${aiConfigId}` },
    { label: "Handoff", path: `/ai-new/settings/handoff/${aiConfigId}` },
    { label: "Test Chat", path: `/ai-new/settings/test-chat/${aiConfigId}` },
    { label: "Knowledge Overrides", path: `/ai-new/settings/knowledge-overrides/${aiConfigId}` },
    { label: "Skills", path: `/ai-new/settings/skills/${aiConfigId}` }
  ];

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;

    const calculateOffset = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const parent = container.parentElement;
      const stickyHeader = parent?.previousElementSibling as HTMLElement | null;

      if (!stickyHeader) {
        setStickyOffset(0);
        return;
      }

      const styles = window.getComputedStyle(stickyHeader);
      const marginBottom = parseFloat(styles.marginBottom || "0");
      const nextOffset = Math.ceil(stickyHeader.getBoundingClientRect().height + marginBottom);

      setStickyOffset((prev) => (prev !== nextOffset ? nextOffset : prev));
    };

    const handleScroll = () => {
      window.requestAnimationFrame(calculateOffset);
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);
    window.addEventListener("scroll", handleScroll, true);

    const container = containerRef.current;
    const parent = container?.parentElement;
    const stickyHeader = parent?.previousElementSibling as HTMLElement | null;

    if (stickyHeader && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => calculateOffset());
      resizeObserver.observe(stickyHeader);
    }

    return () => {
      window.removeEventListener("resize", calculateOffset);
      window.removeEventListener("scroll", handleScroll, true);
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="top-9 sticky z-40 w-full bg-slate-gray-50"
      style={{ top: stickyOffset ? `${stickyOffset}px` : undefined }}
    >
      <div className="flex justify-center border-b-2 border-slate-200 w-full">
        {tabs.map((tab) =>
          tab.disabled ? (
            <span
              key={tab.path}
              className="px-6 py-3 text-sm font-medium border-b-2 -mb-[2px] border-transparent text-slate-300 cursor-not-allowed"
              title="Coming soon"
            >
              {tab.label}
            </span>
          ) : (
            <NavLink
              key={tab.path}
              to={tab.path}
              className={({ isActive }) =>
                `px-6 py-3 text-sm font-medium border-b-2 -mb-[2px] transition-colors ${
                  isActive
                    ? "border-cornflower-blue-500 text-cornflower-blue-500"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`
              }
            >
              {tab.label}
            </NavLink>
          )
        )}
      </div>
      <div className="h-4" aria-hidden="true" />
    </div>
  );
}
